"use client";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import "./ProfileSidebar.css";
import { Avatar } from "antd";

import { axiosGateway } from "../../../api/axios";
import { ensureUploadUrl } from "../../../utils/uploads";

/* =========================
   Helpers (LOGIC ONLY)
========================= */
const extractLinks = (text = "") =>
  Array.from((text || "").matchAll(/\bhttps?:\/\/[^\s<>"')\]]+/gi)).map((m) => m[0]);

const cleanUrl = (u = "") => String(u).split("?")[0].split("#")[0];

const isVideoByName = (n = "") => /\.(mp4|mov|webm|mkv|avi|m4v|ogg)(\.enc)?$/i.test(n);
const isImageByName = (n = "") =>
  /\.(png|jpe?g|gif|webp|bmp|svg|ico|avif)(\.enc)?$/i.test(n);

const isDocByName = (n = "") => /\.(pdf|docx?|xlsx?|pptx?|txt|csv|json|xml|zip|rar)$/i.test(n);

/* =========================
   URL -> axios path (avoid /api/api)
========================= */
function toAxiosGatewayPath(originalUrlOrPath) {
  if (!originalUrlOrPath) return null;

  if (/^(blob:|data:)/i.test(String(originalUrlOrPath).trim())) {
    return null;
  }

  const base = String(axiosGateway?.defaults?.baseURL || "").replace(/\/+$/, "");
  const baseHasApi = /\/api$/i.test(base);

  let pathname = "";
  let search = "";

  if (/^https?:\/\//i.test(originalUrlOrPath)) {
    try {
      const u = new URL(originalUrlOrPath);
      pathname = u.pathname || "";
      search = u.search || "";
    } catch {
      return null;
    }
  } else {
    const s = String(originalUrlOrPath).trim();
    const [p, q] = s.split("?");
    pathname = p || "";
    search = q ? `?${q}` : "";
  }

  if (!pathname.startsWith("/")) pathname = "/" + pathname;

  // normalize duplicated api
  pathname = pathname.replace(/^\/api\/api\//i, "/api/");

  // if axios baseURL already ends with /api => remove leading /api
  if (baseHasApi) {
    pathname = pathname.replace(/^\/api(?=\/)/i, "");
    if (!pathname) pathname = "/";
  } else {
    // otherwise ensure /api is present
    if (!/^\/api(\/|$)/i.test(pathname)) pathname = `/api${pathname}`;
  }

  return `${pathname}${search}`;
}

export default function ProfileSidebar({
  contact,
  messages = [],
  onToggle = () => {},
  loading,
  error,
}) {
  const selectedContact = contact;
  const { t } = useTranslation();

  const [expandedSections, setExpandedSections] = useState({
    images: true,
    documents: false,
    otherFiles: false,
  });

  // ✅ Aucun contact sélectionné
  if (!selectedContact) {
    return <div className="psb-empty">{t("messaging.profileSidebar.noContact")}</div>;
  }

  // ✅ Données sécurisées
  const displayName =
    selectedContact?.name ||
    selectedContact?.fullName ||
    selectedContact?.username ||
    "N/A";

  const email = selectedContact?.email || selectedContact?.meta?.email || "user@example.com";

  const avatar =
    selectedContact?.avatarUrl ||
    selectedContact?.photo ||
    selectedContact?.image ||
    selectedContact?.meta?.avatarUrl ||
    undefined;

  const isOnline = Boolean(selectedContact?.isOnline);

  // ✅ Initiales robustes
  const getInitials = () => {
    if (!displayName || displayName === "N/A") return "?";
    return displayName
      .trim()
      .split(/\s+/)
      .map((n) => n[0])
      .filter(Boolean)
      .join("")
      .toUpperCase()
      .slice(0, 3);
  };

  // ✅ Toggle section
  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  /* ============================================================
     ✅ MEDIA CLASSIFICATION
     - Images  : messageType==="image" OR mime image/* OR image extension
     - Documents: mime application/* OR doc extension (.pdf, .docx, etc.)
     - OtherFiles: everything else with a fileUrl
     ============================================================ */
  const { realImages, realDocuments, realOtherFiles } = useMemo(() => {
    const imgs = [];
    const docs = [];
    const others = [];

    for (const m of messages || []) {
      if (!m) continue;

      const msgType = String(m.messageType || "").toLowerCase();
      const mime = String(m.mimeType || "").toLowerCase();

      // ── Try to get the file URL ──────────────────────────────
      // Some messages carry the image URL inside `content` (e.g. blob/http URL)
      // when fileUrl is absent but messageType === "image"
      let rawFileUrl = m.fileUrl || null;

      if (!rawFileUrl && msgType === "image" && m.content) {
        // extract first URL from content
        const found = extractLinks(m.content);
        if (found.length > 0) rawFileUrl = found[0];
      }

      if (!rawFileUrl) continue;

      const fileUrl = ensureUploadUrl(rawFileUrl);
      const fileName = m.fileName || cleanUrl(fileUrl).split("/").pop() || "";
      const nameForDetect = (fileName || fileUrl).toLowerCase();

      const isImg =
        msgType === "image" ||
        mime.startsWith("image/") ||
        isImageByName(nameForDetect);

      const isDoc =
        mime.startsWith("application/") ||
        isDocByName(nameForDetect);

      const item = { ...m, fileUrl, fileName };

      if (isImg) imgs.push(item);
      else if (isDoc) docs.push(item);
      else others.push(item);
    }

    return { realImages: imgs, realDocuments: docs, realOtherFiles: others };
  }, [messages]);

  /* ============================================================
     ✅ THUMBNAILS FOR .enc (NO UI CHANGE)
     ============================================================ */
  const blobCacheRef = useRef(new Map()); // fileUrl -> blobUrl
  const [, force] = useState(0);

  const getThumbSrc = useCallback((fileUrl) => {
    return blobCacheRef.current.get(fileUrl) || fileUrl;
  }, []);

  useEffect(() => {
    if (!expandedSections.images) return;
    if (!realImages.length) return;

    let cancelled = false;

    (async () => {
      for (const img of realImages) {
        const key = img.fileUrl;
        if (!key) continue;
        if (blobCacheRef.current.has(key)) continue;

        try {
          const normalized = ensureUploadUrl(key);
          const axiosPath = toAxiosGatewayPath(normalized);
          if (!axiosPath) continue;

          const res = await axiosGateway.get(axiosPath, {
            responseType: "blob",
            withCredentials: true,
            validateStatus: () => true,
          });

          if (cancelled) return;
          if (res.status >= 400 || !res.data) continue;

          const blobUrl = URL.createObjectURL(res.data);
          blobCacheRef.current.set(key, blobUrl);
          force((x) => x + 1);
        } catch {
          // ignore per image
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [expandedSections.images, realImages]);

  useEffect(() => {
    return () => {
      try {
        for (const u of blobCacheRef.current.values()) URL.revokeObjectURL(u);
      } catch {}
      blobCacheRef.current.clear();
    };
  }, []);

  return (
    <div className="psb-wrapper">
      {/* Header */}
      <div className="psb-header">
        <button
          className="psb-back-btn"
          onClick={onToggle}
          aria-label={t("messaging.profileSidebar.back")}
          type="button"
        >
          ←
        </button>
        <h2 className="psb-header-title">{t("messaging.profileSidebar.title")}</h2>
      </div>

      {/* Contenu */}
      <div className="psb-content">
        {/* Carte profil */}
        <div className="psb-card">
          <div className="psb-avatar-wrap">
            {avatar ? (
              <>
                <img src={avatar} alt={displayName} className="psb-avatar psb-avatar-image" />
                <div className={`psb-dot ${isOnline ? "on" : "off"}`}></div>
              </>
            ) : (
              <>
                <div className="psb-avatar psb-avatar-fallback">{getInitials()}</div>
                <div className={`psb-dot ${isOnline ? "on" : "off"}`}></div>
              </>
            )}
          </div>

          <div className="psb-name">{displayName}</div>
          <div className="psb-email">{email}</div>
        </div>

        {/* ── IMAGES ──────────────────────────────────────────── */}
        <div className="psb-section">
          <button
            className="psb-section-header"
            onClick={() => toggleSection("images")}
            type="button"
          >
            <div className="psb-section-title">
              <span className="psb-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                  viewBox="0 0 16 16" fill="none">
                  <path d="M12.6667 2H3.33333C2.59695 2 2 2.59695 2 3.33333V12.6667C2 13.403 2.59695 14 3.33333 14H12.6667C13.403 14 14 13.403 14 12.6667V3.33333C14 2.59695 13.403 2 12.6667 2Z" stroke="#1e1e1e" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M5.99935 7.33366C6.73573 7.33366 7.33268 6.73671 7.33268 6.00033C7.33268 5.26395 6.73573 4.66699 5.99935 4.66699C5.26297 4.66699 4.66602 5.26395 4.66602 6.00033C4.66602 6.73671 5.26297 7.33366 5.99935 7.33366Z" stroke="#1e1e1e" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 10.0004L11.9427 7.94312C11.6926 7.69315 11.3536 7.55273 11 7.55273C10.6464 7.55273 10.3074 7.69315 10.0573 7.94312L4 14.0004" stroke="#1e1e1e" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              <span>{t("messaging.profileSidebar.sections.images")}</span>
              {realImages.length > 0 && (
                <span className="psb-count">{realImages.length}</span>
              )}
            </div>
            <span className={`psb-toggle ${expandedSections.images ? "expanded" : ""}`}>
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                viewBox="0 0 18 18" fill="none">
                <path d="M4.5 6.75L9 11.25L13.5 6.75" stroke="#1e1e1e" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
          </button>

          {expandedSections.images && (
            <div className="psb-section-content">
              <div className="psb-images-grid">
                {realImages.length === 0 ? (
                  <div className="psb-empty-section">{t("messaging.profileSidebar.emptyImages")}</div>
                ) : (
                  realImages.map((img) => (
                    <div key={img.id || img._id || img.fileUrl} className="psb-image-item">
                      <img
                        src={getThumbSrc(img.fileUrl)}
                        alt={img.fileName || "image"}
                        className="psb-image-thumbnail"
                        onError={(e) => {
                          e.currentTarget.src = img.fileUrl;
                        }}
                      />
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* ── DOCUMENTS ───────────────────────────────────────── */}
        <div className="psb-section">
          <button className="psb-section-header" onClick={() => toggleSection("documents")} type="button">
            <div className="psb-section-title">
              <span className="psb-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                  viewBox="0 0 16 16" fill="none">
                  <path d="M9.99935 1.33301H3.99935C3.64573 1.33301 3.30659 1.47348 3.05654 1.72353C2.80649 1.97358 2.66602 2.31272 2.66602 2.66634V13.333C2.66602 13.6866 2.80649 14.0258 3.05654 14.2758C3.30659 14.5259 3.64573 14.6663 3.99935 14.6663H11.9993C12.353 14.6663 12.6921 14.5259 12.9422 14.2758C13.1922 14.0258 13.3327 13.6866 13.3327 13.333V4.66634L9.99935 1.33301Z" stroke="#1e1e1e" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9.33398 1.33301V3.99967C9.33398 4.3533 9.47446 4.69244 9.72451 4.94248C9.97456 5.19253 10.3137 5.33301 10.6673 5.33301H13.334" stroke="#1e1e1e" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M6.66732 6H5.33398" stroke="#1e1e1e" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M10.6673 8.66699H5.33398" stroke="#1e1e1e" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M10.6673 11.333H5.33398" stroke="#1e1e1e" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              <span>{t("messaging.profileSidebar.sections.documents")}</span>
              {realDocuments.length > 0 && (
                <span className="psb-count">{realDocuments.length}</span>
              )}
            </div>
            <span className={`psb-toggle ${expandedSections.documents ? "expanded" : ""}`}>
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                viewBox="0 0 18 18" fill="none">
                <path d="M4.5 6.75L9 11.25L13.5 6.75" stroke="#1e1e1e" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
          </button>

          {expandedSections.documents && (
            <div className="psb-section-content">
              {realDocuments.length === 0 ? (
                <div className="psb-empty-section">{t("messaging.profileSidebar.emptyDocuments")}</div>
              ) : (
                realDocuments.map((doc) => (
                  <a
                    key={doc.id || doc._id || doc.fileUrl}
                    className="psb-list-item"
                    href={doc.fileUrl}
                    target="_blank"
                    rel="noreferrer"
                    title={doc.fileName || cleanUrl(doc.fileUrl).split("/").pop()}
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    <span className="psb-item-icon">📄</span>
                    <span className="psb-item-name">
                      {doc.fileName || cleanUrl(doc.fileUrl).split("/").pop()}
                    </span>
                  </a>
                ))
              )}
            </div>
          )}
        </div>

        {/* ── OTHER FILES ─────────────────────────────────────── */}
        <div className="psb-section">
          <button className="psb-section-header" onClick={() => toggleSection("otherFiles")} type="button">
            <div className="psb-section-title">
              <span className="psb-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                  viewBox="0 0 16 16" fill="none">
                  <path d="M2.66602 14.6663H11.9993C12.353 14.6663 12.6921 14.5259 12.9422 14.2758C13.1922 14.0258 13.3327 13.6866 13.3327 13.333V4.66634L9.99935 1.33301H3.99935C3.64573 1.33301 3.30659 1.47348 3.05654 1.72353C2.80649 1.97358 2.66602 2.31272 2.66602 2.66634V5.33301" stroke="#1e1e1e" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9.33398 1.33301V3.99967C9.33398 4.3533 9.47446 4.69244 9.72451 4.94248C9.97456 5.19253 10.3137 5.33301 10.6673 5.33301H13.334" stroke="#1e1e1e" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 10H6" stroke="#1e1e1e" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M4 8V12" stroke="#1e1e1e" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              <span>{t("messaging.profileSidebar.sections.otherFiles")}</span>
              {realOtherFiles.length > 0 && (
                <span className="psb-count">{realOtherFiles.length}</span>
              )}
            </div>
            <span className={`psb-toggle ${expandedSections.otherFiles ? "expanded" : ""}`}>
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                viewBox="0 0 18 18" fill="none">
                <path d="M4.5 6.75L9 11.25L13.5 6.75" stroke="#1e1e1e" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
          </button>

          {expandedSections.otherFiles && (
            <div className="psb-section-content">
              {realOtherFiles.length === 0 ? (
                <div className="psb-empty-section">{t("messaging.profileSidebar.emptyFiles")}</div>
              ) : (
                realOtherFiles.map((f) => (
                  <a
                    key={f.id || f._id || f.fileUrl}
                    className="psb-list-item"
                    href={f.fileUrl}
                    target="_blank"
                    rel="noreferrer"
                    title={f.fileName || cleanUrl(f.fileUrl).split("/").pop()}
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    <span className="psb-item-icon">📎</span>
                    <span className="psb-item-name">
                      {f.fileName || cleanUrl(f.fileUrl).split("/").pop()}
                    </span>
                  </a>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}